document.addEventListener('DOMContentLoaded', () => {
  initPeltierThemeModes();
  initPeltierBackgroundFade();
});

document.addEventListener('shopify:section:load', () => {
  initPeltierThemeModes();
  initPeltierBackgroundFade();
});

window.addEventListener('peltier:theme-mode-change', () => {
  initPeltierBackgroundFade();
});

/* =========================
   THEME MODES (LIGHT / DARK / BLUR)
========================= */

function initPeltierThemeModes() {
  const html = document.documentElement;
  const body = document.body;
  if (!body) return;

  const buttons = document.querySelectorAll('[data-mode-button]');
  if (!buttons.length) return;

  const blurEnabled = body.dataset.blurModeEnabled === 'true';
  const storageKey = 'peltierThemeMode';

  function getAllowedModes() {
    return blurEnabled ? ['light', 'dark', 'blur'] : ['light', 'dark'];
  }

  function getStoredMode() {
    const stored = localStorage.getItem(storageKey);
    const allowed = getAllowedModes();
    if (stored && allowed.includes(stored)) return stored;
    return 'light';
  }

  function setStoredMode(mode) {
    localStorage.setItem(storageKey, mode);
  }

  function clearThemeClasses() {
    html.classList.remove('light', 'dark', 'blur', 'theme-light', 'theme-dark', 'theme-blur');
    body.classList.remove('light', 'dark', 'blur', 'theme-light', 'theme-dark', 'theme-blur');
    html.classList.remove('dark');
    body.classList.remove('dark');
  }

  function applyMode(mode) {
    const allowed = getAllowedModes();
    const finalMode = allowed.includes(mode) ? mode : 'light';

    clearThemeClasses();

    html.dataset.themeMode = finalMode;
    body.dataset.themeMode = finalMode;

    html.classList.add(finalMode, `theme-${finalMode}`);
    body.classList.add(finalMode, `theme-${finalMode}`);

    if (finalMode === 'dark') {
      html.classList.add('dark');
      body.classList.add('dark');
    }

    buttons.forEach((button) => {
      const isActive = button.dataset.modeButton === finalMode;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    setStoredMode(finalMode);

    window.dispatchEvent(
      new CustomEvent('peltier:theme-mode-change', {
        detail: { mode: finalMode }
      })
    );

    if (finalMode === 'blur') {
      window.dispatchEvent(new CustomEvent('peltier:trigger-blur-now'));
    }
  }

  buttons.forEach((button) => {
    if (button.dataset.modeBound === 'true') return;
    button.dataset.modeBound = 'true';

    button.addEventListener('click', () => {
      applyMode(button.dataset.modeButton);
    });
  });

  applyMode(getStoredMode());
}

/* =========================
   GLOBAL BLUR BACKGROUND SYSTEM
========================= */

window.addEventListener('peltier:trigger-blur-now', () => {
  forceInitialBlurGradient();
});

function initPeltierBackgroundFade() {
  const body = document.body;
  if (!body) return;

  const bgStage = document.querySelector('[data-global-bg-stage]');
  if (!bgStage) return;

  const layerA = bgStage.querySelector('.peltier-bg-layer-a');
  const layerB = bgStage.querySelector('.peltier-bg-layer-b');
  if (!layerA || !layerB) return;

  const satMin = clampNumber(parseFloat(body.dataset.blurSatMin), 0, 100, 8);
  const satMax = clampNumber(parseFloat(body.dataset.blurSatMax), 0, 100, 18);
  const lightMin = clampNumber(parseFloat(body.dataset.blurLightMin), 0, 100, 38);
  const lightMax = clampNumber(parseFloat(body.dataset.blurLightMax), 0, 100, 52);
  const blur = clampNumber(parseFloat(body.dataset.blurStrength), 20, 200, 96);
  const fadeSpeed = clampNumber(parseFloat(body.dataset.blurFadeSpeed), 150, 3000, 700);

  layerA.style.filter = `blur(${blur}px)`;
  layerB.style.filter = `blur(${blur}px)`;
  layerA.style.transitionDuration = `${fadeSpeed}ms`;
  layerB.style.transitionDuration = `${fadeSpeed}ms`;

  let activeLayer = layerA;
  let inactiveLayer = layerB;

  function isBlurMode() {
    return document.documentElement.dataset.themeMode === 'blur' || body.dataset.themeMode === 'blur';
  }

  function applyRandomMutedGradient() {
    if (!isBlurMode()) return;

    const gradient = buildMutedGradient({
      satMin,
      satMax,
      lightMin,
      lightMax
    });

    inactiveLayer.style.background = gradient;
    inactiveLayer.classList.add('is-active');
    activeLayer.classList.remove('is-active');

    const temp = activeLayer;
    activeLayer = inactiveLayer;
    inactiveLayer = temp;
  }

  function bindCardListeners() {
    const cards = document.querySelectorAll(
      '[data-product-card], .peltier-collection-grid__item, .product-grid__item, .card-wrapper'
    );

    cards.forEach((card) => {
      if (card.dataset.blurBound === 'true') return;
      card.dataset.blurBound = 'true';

      card.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover: hover)').matches) {
          applyRandomMutedGradient();
        }
      });

      card.addEventListener('click', applyRandomMutedGradient);
      card.addEventListener('focusin', applyRandomMutedGradient);
      card.addEventListener('touchstart', applyRandomMutedGradient, { passive: true });
    });
  }

  bindCardListeners();

  if (bgStage.dataset.globalBlurObserverBound !== 'true') {
    bgStage.dataset.globalBlurObserverBound = 'true';

    const observer = new MutationObserver(() => {
      bindCardListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (isBlurMode()) {
    applyRandomMutedGradient();
  }
}

/* =========================
   FORCE INITIAL BLUR
========================= */

function forceInitialBlurGradient() {
  const body = document.body;
  if (!body) return;

  const bgStage = document.querySelector('[data-global-bg-stage]');
  if (!bgStage) return;

  const layerA = bgStage.querySelector('.peltier-bg-layer-a');
  const layerB = bgStage.querySelector('.peltier-bg-layer-b');
  if (!layerA || !layerB) return;

  const satMin = clampNumber(parseFloat(body.dataset.blurSatMin), 0, 100, 8);
  const satMax = clampNumber(parseFloat(body.dataset.blurSatMax), 0, 100, 18);
  const lightMin = clampNumber(parseFloat(body.dataset.blurLightMin), 0, 100, 38);
  const lightMax = clampNumber(parseFloat(body.dataset.blurLightMax), 0, 100, 52);

  const gradient = buildMutedGradient({
    satMin,
    satMax,
    lightMin,
    lightMax
  });

  if (layerA.classList.contains('is-active')) {
    layerB.style.background = gradient;
    layerB.classList.add('is-active');
    layerA.classList.remove('is-active');
  } else {
    layerA.style.background = gradient;
    layerA.classList.add('is-active');
    layerB.classList.remove('is-active');
  }
}

/* =========================
   GRADIENT GENERATOR
========================= */

function buildMutedGradient({ satMin, satMax, lightMin, lightMax }) {
  const baseHue = randomInt(0, 359);

  const hue1 = normalizeHue(baseHue + randomInt(-18, 18));
  const hue2 = normalizeHue(baseHue + randomInt(12, 42));
  const hue3 = normalizeHue(baseHue + randomInt(-42, -12));

  const sat1 = randomInt(satMin, satMax);
  const sat2 = randomInt(satMin, satMax);
  const sat3 = randomInt(satMin, satMax);

  const light1 = randomInt(lightMin, lightMax);
  const light2 = randomInt(lightMin, lightMax);
  const light3 = randomInt(lightMin, lightMax);

  const color1 = `hsl(${hue1} ${sat1}% ${light1}%)`;
  const color2 = `hsl(${hue2} ${sat2}% ${light2}%)`;
  const color3 = `hsl(${hue3} ${sat3}% ${light3}%)`;

  return `
    radial-gradient(circle at 18% 22%, ${color1} 0%, transparent 52%),
    radial-gradient(circle at 78% 34%, ${color2} 0%, transparent 50%),
    radial-gradient(circle at 52% 78%, ${color3} 0%, transparent 48%),
    linear-gradient(135deg, rgba(255,255,255,0.04), rgba(0,0,0,0.03))
  `;
}

/* =========================
   HELPERS
========================= */

function randomInt(min, max) {
  const low = Math.ceil(Math.min(min, max));
  const high = Math.floor(Math.max(min, max));
  return Math.floor(Math.random() * (high - low + 1)) + low;
}

function normalizeHue(hue) {
  let h = hue % 360;
  if (h < 0) h += 360;
  return h;
}

function clampNumber(value, min, max, fallback) {
  if (Number.isNaN(value)) return fallback;
  return Math.min(Math.max(value, min), max);
}